import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ApiError, apiClient } from '../../lib/api';
import { isAuthenticated, saveSession } from '../../lib/auth';
import styles from './Auth.module.css';

interface StartResponse {
  challenge_id: string;
  expires_at: string;
}

interface PollResponse {
  state: 'pending' | 'approved' | 'rejected' | 'expired' | 'claimed';
  session_token?: string | null;
  user_email?: string | null;
}

type Stage = 'loading' | 'waiting' | 'approved' | 'expired' | 'error';

const POLL_INTERVAL_MS = 2000;

export function LoginPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('loading');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const pollTimer = useRef<number | null>(null);
  const tickTimer = useRef<number | null>(null);

  useEffect(() => {
    void start();
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isAuthenticated()) {
    return <Navigate to="/chat" replace />;
  }

  function cleanup() {
    if (pollTimer.current != null) window.clearInterval(pollTimer.current);
    if (tickTimer.current != null) window.clearInterval(tickTimer.current);
    pollTimer.current = null;
    tickTimer.current = null;
  }

  async function start() {
    cleanup();
    setStage('loading');
    setError(null);
    try {
      const r = await apiClient.post<StartResponse>(
        '/auth/desktop/start',
        undefined,
        { anonymous: true },
      );
      setChallengeId(r.challenge_id);
      setStage('waiting');
      pollTimer.current = window.setInterval(
        () => void poll(r.challenge_id),
        POLL_INTERVAL_MS,
      );
      tickTimer.current = window.setInterval(() => {
        const left = Math.max(
          0,
          Math.floor((new Date(r.expires_at).getTime() - Date.now()) / 1000),
        );
        setSecondsLeft(left);
        if (left === 0) {
          cleanup();
          setStage('expired');
        }
      }, 250);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      setError(msg || 'Не удалось создать запрос на вход.');
      setStage('error');
    }
  }

  async function poll(id: string) {
    try {
      const r = await apiClient.get<PollResponse>(
        `/auth/desktop/poll?challenge_id=${encodeURIComponent(id)}`,
        { anonymous: true },
      );
      if (r.state === 'approved' && r.session_token) {
        cleanup();
        const ttl = 1000 * 60 * 60 * 24;
        saveSession({
          token: r.session_token,
          expires_at: new Date(Date.now() + ttl).toISOString(),
          email: r.user_email ?? '',
        });
        setStage('approved');
        window.setTimeout(() => navigate('/chat', { replace: true }), 600);
      } else if (r.state === 'expired') {
        cleanup();
        setStage('expired');
      } else if (r.state === 'rejected') {
        cleanup();
        setError('Запрос на вход отклонён с телефона.');
        setStage('error');
      }
    } catch {
      // transient — keep polling
    }
  }

  const mmss = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

  return (
    <main className={styles.surface}>
      <article className={styles.card}>
        <div className={styles.eyebrow}>Вход</div>
        <h1 className={styles.title}>
          Линза <em>·</em> вход
        </h1>
        <p className={styles.lede}>
          Открой Линзу на телефоне → «Войти на другом устройстве» → сосканируй
          этот QR. Десктоп пустим сразу после подтверждения.
        </p>

        {stage === 'loading' && <p className={styles.sentText}>Готовлю запрос…</p>}

        {stage === 'waiting' && challengeId && (
          <div className={styles.qrWrap}>
            <div className={styles.qrFrame}>
              <QRCodeSVG
                value={challengeId}
                size={256}
                level="M"
                bgColor="#F7F0E0"
                fgColor="#14110D"
                marginSize={2}
              />
            </div>
            <p className={styles.qrCaption}>
              истекает через <span className={styles.timer}>{mmss}</span>
            </p>
          </div>
        )}

        {stage === 'approved' && (
          <div className={styles.qrWrap}>
            <p className={styles.qrApproved}>Подтверждено · открываю…</p>
          </div>
        )}

        {stage === 'expired' && (
          <div className={styles.qrWrap}>
            <p className={styles.sentText}>QR истёк.</p>
            <button className={styles.linkBtn} onClick={() => void start()}>
              ОБНОВИТЬ
            </button>
          </div>
        )}

        {stage === 'error' && error && (
          <div className={styles.qrWrap}>
            <p className={styles.errorText}>{error}</p>
            <button className={styles.linkBtn} onClick={() => void start()}>
              ПОПРОБОВАТЬ СНОВА
            </button>
          </div>
        )}
      </article>
    </main>
  );
}
